import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { RecordContext } from '@rygen/rygen-ui/model/aiAssistant';

import { Order } from '@/model/order/Order';
import { Shipment } from '@/model/shipment/Shipment';
import { useParentEntityStore } from '@/stores/ParentEntity';

export interface UseRecordContextResponse {
  label: ComputedRef<string | undefined>;
  record: ComputedRef<RecordContext | null>;
}

const LABEL_KEY: Partial<Record<RecordContext['type'], string>> = {
  ORDER: 'orders.orders',
  SHIPMENT: 'shipments.shipments',
};

// Shapes the currently viewed record for the assistant
export const useRecordContext = (): UseRecordContextResponse => {
  const { t } = useI18n();
  const { parentEntity } = useParentEntityStore();

  const record = computed<RecordContext | null>(() => {
    const entity = parentEntity.value;
    if (entity instanceof Order && entity.id != null) {
      return { type: 'ORDER', id: String(entity.id), fields: { ...entity } };
    }
    if (entity instanceof Shipment && entity.id != null) {
      return { type: 'SHIPMENT', id: String(entity.id), fields: { ...entity } };
    }
    return null;
  });

  const label = computed(() => {
    if (!record.value) {
      return undefined;
    }
    const key = LABEL_KEY[record.value.type];
    return key ? `${t(key)} #${record.value.id}` : undefined;
  });

  return { label, record };
};
